// src/components/research/ResearchArticle.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet';
import { FaFacebook, FaXTwitter, FaLink } from 'react-icons/fa6';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import styles from './ResearchArticle.module.css';

// Simple reading-time estimator: ~200 words/min
function estimateReadingTime(text = '') {
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / 200);
}

// A Markdown image renderer that enforces alt text & lazy loads
const MarkdownImage = ({ alt, src, title }) => (
  <img
    src={src}
    alt={alt || 'Article image'}
    title={title}
    loading="lazy"
    style={{ maxWidth: '100%', borderRadius: 'var(--radius)' }}
  />
);

const ResearchArticle = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  const pageUrl = useMemo(() => window.location.href, []);
  const readingTime = useMemo(
    () => article ? estimateReadingTime(article.message) : 0,
    [article]
  );

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'researchArticles', slug));
        if (!snap.exists()) throw new Error('Article not found.');
        setArticle(snap.data());
      } catch (e) {
        console.error(e);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {/* ignore */}
  };

  const nowISO = new Date().toISOString();
  const publishedISO = article?.date
    ? new Date(article.date).toISOString()
    : article?.timestamp?.toDate().toISOString();

  // JSON-LD for Article + BreadcrumbList + Speakable
  const schema = useMemo(() => article && ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://novionix.ai" },
          { "@type": "ListItem", "position": 2, "name": "Research", "item": "https://novionix.ai/research" },
          { "@type": "ListItem", "position": 3, "name": article.title, "item": pageUrl }
        ]
      },
      {
        "@type": "Article",
        "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl },
        "headline": article.title,
        "image": [ article.imageUrl ],
        "datePublished": publishedISO,
        "dateModified": nowISO,
        "author": {
          "@type": article.author ? "Person" : "Organization",
          "name": article.author || "NOVIONIX Labs"
        },
        "publisher": {
          "@type": "Organization",
          "name": "NOVIONIX Labs",
          "logo": { "@type": "ImageObject", "url": "https://novionix.ai/logo.png" }
        },
        "description": article.summary,
        "articleSection": article.category || "Research",
        "keywords": article.tags,
        "wordCount": article.message.trim().split(/\s+/).length,
        "speakable": {
          "@type": "SpeakableSpecification",
          "xpath": [ "/html/head/title", "//div[@class='content']" ]
        }
      }
    ]
  }), [article, pageUrl, publishedISO, nowISO]);

  if (loading) return <div className={styles.loader}><p>Loading…</p></div>;
  if (!article) return (
    <div className={styles.error}>
      <p>Article not found.</p>
      <Link to="/research">← Back to Research</Link>
    </div>
  );

  // Optionally, implement prev/next if you support chronological nav
  const prevUrl = null; // e.g. get from your API
  const nextUrl = null;

  return (
    <div className={styles.container}>
      <Helmet>
        {/* Title + Description */}
        <title>{article.title} | NOVIONIX Labs Research</title>
        <meta name="description" content={article.summary} />
        <meta name="keywords" content={article.tags?.join(', ')} />
        <meta name="robots" content="index, follow" />

        {/* Article metas */}
        <meta property="article:published_time" content={publishedISO} />
        <meta property="article:modified_time"  content={nowISO} />
        <meta property="article:section"        content={article.category || 'Research'} />
        {article.tags?.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* OpenGraph */}
        <meta property="og:type"        content="article" />
        <meta property="og:title"       content={article.title} />
        <meta property="og:description" content={article.summary} />
        <meta property="og:url"         content={pageUrl} />
        <meta property="og:image"       content={article.imageUrl} />

        {/* Twitter */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@NOVIONIXLabs" />
        <meta name="twitter:creator"     content={article.authorTwitter || '@NOVIONIXLabs'} />
        <meta name="twitter:title"       content={article.title} />
        <meta name="twitter:description" content={article.summary} />
        <meta name="twitter:image"       content={article.imageUrl} />

        {/* Canonical & pagination */}
        <link rel="canonical" href={pageUrl} />
        {prevUrl && <link rel="prev" href={prevUrl} />}
        {nextUrl && <link rel="next" href={nextUrl} />}

        {/* Reading time */}
        <meta name="twitter:label1" content="Reading time" />
        <meta name="twitter:data1"  content={`${readingTime} min read`} />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </script>
      </Helmet>

      <nav className={styles.breadcrumb}>
        <Link to="/">Home</Link> › <Link to="/research">Research</Link> ›{' '}
        <span aria-current="page">{article.title}</span>
      </nav>

      <header className={styles.header}>
        <h1>{article.title}</h1>
        <div className={styles.subMeta}>
          {article.author && <span>By {article.author}</span>}
          {article.date && (
          <span> • {article.date.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')}</span>
          )}
          <span> • {readingTime} min read</span>
        </div>
        <div className={styles.meta}>
          {/* share buttons… */}
          <a href={`https://facebook.com/sharer/sharer.php?u=${pageUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook"><FaFacebook/></a>
          <a href={`https://twitter.com/intent/tweet?url=${pageUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Tweet this"><FaXTwitter/></a>
          <button onClick={handleCopyLink} aria-label="Copy link"><FaLink/></button>
          {copySuccess && <span className={styles.copySuccess}>Copied!</span>}
        </div>
      </header>

      <article className={styles.content}>
        <ReactMarkdown
          components={{ img: MarkdownImage }}
        >
          {article.message}
        </ReactMarkdown>
      </article>

      {article.tags?.length > 0 && (
        <div className={styles.tagContainer}>
          {article.tags.map(tag => (
            <Link key={tag} to={`/research/tag/${encodeURIComponent(tag)}`} className={styles.tag}>#{tag}</Link>
          ))}
        </div>
      )}

      <footer className={styles.backLink}>
        <Link to="/research">← Back to Research</Link>
      </footer>
    </div>
  );
};

export default ResearchArticle;
